import { useState } from 'react';
import { net } from '../net';

/** An invite link is just "/?invite=CODE" -- read it once on load. */
function inviteFromUrl(): string {
  try {
    return new URLSearchParams(window.location.search).get('invite')?.trim().toUpperCase() ?? '';
  } catch {
    return '';
  }
}

export function Auth() {
  const initialInvite = inviteFromUrl();
  // Arriving through an invite link means signing up, not signing in.
  const [mode, setMode] = useState<'login' | 'register'>(initialInvite ? 'register' : 'login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [invite, setInvite] = useState(initialInvite);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'login') {
        await net.login(username, password);
      } else {
        await net.register(username, password, name || username, invite.trim().toUpperCase());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth-card">
        <span className="ball-mark auth-logo" aria-hidden="true" />
        <h1>Family Games</h1>
        <p className="sub">
          {mode === 'login'
            ? 'Sign in to play.'
            : initialInvite
              ? 'You have been invited. Pick a username and password.'
              : 'You need an invite code.'}
        </p>

        <form onSubmit={submit}>
          <label>
            Username
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              required
            />
          </label>

          {mode === 'register' && (
            <>
              <label>
                Display name
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="What people see"
                  maxLength={24}
                />
              </label>
              <label>
                Invite code
                <input
                  value={invite}
                  onChange={(e) => setInvite(e.target.value)}
                  placeholder="ABCD-EFGH"
                  autoCapitalize="characters"
                  required
                />
              </label>
            </>
          )}

          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
            />
          </label>

          {error && <div className="form-error">{error}</div>}

          <button type="submit" className="primary" disabled={busy}>
            {busy ? 'One moment…' : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>
        </form>

        <button
          className="link"
          onClick={() => {
            setMode(mode === 'login' ? 'register' : 'login');
            setError(null);
          }}
        >
          {mode === 'login' ? 'I have an invite code' : 'I already have an account'}
        </button>
      </div>
    </div>
  );
}
